import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useTranslation } from 'react-i18next';
import type { SEPayOverduePayload } from '@/api';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { QueryParams, QueryParamValues } from '@/constants/QueryParam';
import { usePayOverdueMutation } from '@/features/products/components/notifications/OverdueNotification/usePayOverdueMutation';
import { routeMap } from '@/routes/routeMap';
import type { OverdueInfoResponse } from '@/types/credit';
import Button from '@/ui-kit/components/Button/Button';
import { DataList, DataListItem, DataListLabel, DataListValue } from '@/ui-kit/components/DataList/DataList';
import { constructURL } from '@/utils/url';

type Payload = {
  overdueInfo: Pick<OverdueInfoResponse, 'totalOverdue'>;
};
type Props = ModalProps<Payload>;

export const getOverduePaymentReturnUrl = ({
  isSuccessful,
  amount,
}: {
  isSuccessful: boolean;
  amount: number;
}) =>
  constructURL(routeMap.credit.root, {
    queryParams: {
      [QueryParams.DEPOSIT_AMOUNT]: `${amount}`,
      [QueryParams.STATUS]: isSuccessful
        ? QueryParamValues.PAY_OVERDUE_SUCCESS
        : QueryParamValues.PAY_OVERDUE_FAIL,
    },
  });

const OverduePayment = ({ closeModal, payload }: Props) => {
  const { t } = useTranslation(['Modal', 'Common']);
  const {
    overdueInfo: { totalOverdue },
  } = payload;

  const overduePayload: SEPayOverduePayload = {
    amount: totalOverdue.amount,
    successfulUrl: getOverduePaymentReturnUrl({ isSuccessful: true, amount: totalOverdue.amount }),
    failedUrl: getOverduePaymentReturnUrl({ isSuccessful: false, amount: totalOverdue.amount }),
  };

  const { mutate: payOverdue, isPending: isPayOverduePending } = usePayOverdueMutation({
    onSettled: closeModal,
  });

  return (
    <Modal title={t('OverduePayment.title')}>
      <div className="flex flex-col gap-4">
        <p>{t('OverduePayment.info', totalOverdue)}</p>
        <DataList>
          <DataListItem>
            <DataListLabel>{t('OverduePayment.infoItemTitle')}</DataListLabel>
            <DataListValue>{t('Common:money', totalOverdue)}</DataListValue>
          </DataListItem>
        </DataList>
      </div>
      <ModalActions>
        <Button variant="primary" loading={isPayOverduePending} onClick={() => payOverdue(overduePayload)}>
          {t('OverduePayment.button', totalOverdue)}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default OverduePayment;
